import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Tabs, Empty, Spin, Table, Pagination } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import { Box, Text } from 'atoms';
import { searchPendingApprovals } from 'store/approvalSliceV2';
import VerificationApprovalCardV2 from './VerificationApprovalCardV2';
import VerificationDecisionModals from './VerificationDecisionModals';
import { VERIFICATION_KINDS } from './verificationConfig';

const PAGE_SIZE = 20;

const PageContainer = styled(Box)`
	max-width: 1600px;
	margin: 0 auto;
	padding: 24px;
`;

const PageTitle = styled(Text)`
	font-size: 24px;
	font-weight: 600;
	color: #333;
	display: block;
	margin-bottom: 4px;
`;

const PageSubtitle = styled(Text)`
	font-size: 14px;
	color: #707070;
	display: block;
`;

const StyledTabs = styled(Tabs)`
	margin-top: 24px;

	.ant-tabs-tab {
		font-size: 16px;
		font-weight: 500;
		padding: 12px 24px;
	}

	.ant-tabs-tab-active .ant-tabs-tab-btn {
		color: #1890ff;
		font-weight: 600;
	}

	.ant-tabs-ink-bar {
		background: #1890ff;
	}
`;

const Content = styled(Box)`
	margin-top: 24px;
`;

const CenterBox = styled(Box)`
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 60px 20px;
`;

const PaginationBar = styled(Box)`
	display: flex;
	justify-content: flex-end;
	margin-top: 16px;
`;

const StyledTable = styled(Table)`
	.ant-table-thead > tr > th {
		background-color: #18405d;
		color: #fff;
		font-weight: 600;
	}

	.ant-table-tbody > tr:nth-child(even) {
		background-color: #f5f5f5;
	}

	.ant-table-tbody > tr:hover {
		background-color: #e6f7ff;
	}
`;

const formatDateTime = (value) => (value ? moment(value).format('DD-MM-YYYY hh:mm A') : '-');

export default function VerificationApprovalPageV2({ kind }) {
	const config = VERIFICATION_KINDS[kind];
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState('pending');
	const [page, setPage] = useState(1);
	const [decision, setDecision] = useState({ mode: null, approval: null });
	const { approvals, loading, actionLoading, pagination } = useSelector(
		(state) => state.approvalStateV2
	);

	const fetchApprovals = useCallback(
		(status, pageNumber) =>
			dispatch(
				searchPendingApprovals({
					filter_by: { approval_type: config.approvalType, status },
					pagination: { page: pageNumber, per_page: PAGE_SIZE },
				})
			),
		[dispatch, config.approvalType]
	);

	useEffect(() => {
		fetchApprovals(activeTab, page);
	}, [activeTab, page, fetchApprovals]);

	const handleTabChange = (key) => {
		setPage(1);
		setActiveTab(key);
	};

	const handleView = (approval) =>
		navigate(`${config.listPath}/${approval.id}`, {
			state: { fromApproval: true, approvalId: approval.id, entityId: approval.entity_id },
		});

	const closeDecision = () => setDecision({ mode: null, approval: null });

	const handleConfirm = async (text) => {
		const { mode, approval } = decision;
		if (!approval) return;
		const result =
			mode === 'approve'
				? await dispatch(config.approve({ approval_id: approval.id, comments: text }))
				: await dispatch(
						config.reject({ approval_id: approval.id, rejection_reason: text })
				  );
		if (result.success) {
			closeDecision();
			fetchApprovals(activeTab, page);
		}
	};

	const tableColumns = useMemo(() => {
		const isRejected = activeTab === 'rejected';
		const sample = config.fields({});
		return [
			...sample.map((field) => ({
				title: field.label,
				key: field.key,
				render: (_, row) =>
					config.fields(row.entity_summary).find((f) => f.key === field.key)?.value,
			})),
			{
				title: 'Requested By',
				key: 'requested_by',
				render: (_, row) => row.requested_by_name || '-',
			},
			{
				title: isRejected ? 'Rejected By' : 'Approved By',
				key: 'reviewer',
				render: (_, row) => (isRejected ? row.rejected_by_name : row.approver_name) || '-',
			},
			{
				title: isRejected ? 'Rejected On' : 'Approved On',
				key: 'reviewed_on',
				render: (_, row) => formatDateTime(isRejected ? row.rejected_at : row.approved_at),
			},
			{
				title: isRejected ? 'Reason' : 'Comments',
				key: 'note',
				render: (_, row) => (isRejected ? row.rejection_reason : row.comments) || '-',
			},
			{
				title: 'Documents',
				key: 'view',
				render: (_, row) => (
					<EyeOutlined
						style={{ cursor: 'pointer', fontSize: 18 }}
						onClick={() => handleView(row)}
					/>
				),
			},
		];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab, config]);

	const renderContent = (tabKey) => {
		if (tabKey !== activeTab) return null;
		if (loading) {
			return (
				<CenterBox>
					<Spin size="large" />
				</CenterBox>
			);
		}
		if (!approvals || approvals.length === 0) {
			return (
				<CenterBox>
					<Empty description={`No ${tabKey} ${config.noun.toLowerCase()} approvals`} />
				</CenterBox>
			);
		}
		return (
			<Content>
				{tabKey === 'pending' ? (
					approvals.map((approval) => (
						<VerificationApprovalCardV2
							key={approval.id}
							fields={config.fields(approval.entity_summary)}
							aclModule={config.aclModule}
							onView={() => handleView(approval)}
							onApprove={() => setDecision({ mode: 'approve', approval })}
							onReject={() => setDecision({ mode: 'reject', approval })}
						/>
					))
				) : (
					<StyledTable
						rowKey="id"
						columns={tableColumns}
						dataSource={approvals}
						pagination={false}
						scroll={{ x: 'max-content' }}
					/>
				)}
				{pagination?.total > PAGE_SIZE && (
					<PaginationBar>
						<Pagination
							current={page}
							pageSize={PAGE_SIZE}
							total={pagination.total}
							showSizeChanger={false}
							onChange={setPage}
						/>
					</PaginationBar>
				)}
			</Content>
		);
	};

	const tabItems = ['pending', 'approved', 'rejected'].map((key) => ({
		key,
		label: key.charAt(0).toUpperCase() + key.slice(1),
		children: renderContent(key),
	}));

	return (
		<PageContainer>
			<PageTitle>{config.title}</PageTitle>
			<PageSubtitle>{config.subtitle}</PageSubtitle>
			<StyledTabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} />
			<VerificationDecisionModals
				mode={decision.mode}
				noun={config.noun}
				loading={actionLoading}
				onConfirm={handleConfirm}
				onClose={closeDecision}
			/>
		</PageContainer>
	);
}

VerificationApprovalPageV2.propTypes = {
	kind: PropTypes.oneOf(['driver', 'vehicle']).isRequired,
};
